import dayjs from 'dayjs';
import {
  ReliabilityScoreInfluence,
  ReliabilityScoreInfluenceDirection,
} from '@core/generated/gql-types';

export type ReliabilityJobRow = {
  id: string;
  jobId: string;
  date: string;
  clientName: string;
  skillSet: string;
  industry: string;
  postedDate: string;
  direction: ReliabilityScoreInfluenceDirection;
};

type ReliabilityLookups = {
  getClientName: (clientId: string) => string | undefined;
  getClassification: (
    classificationId: string,
  ) =>
    | { name?: string | null; classificationGroupId?: string | null }
    | null
    | undefined;
  getIndustryName: (classificationGroupId: string) => string | undefined;
};

const parseReliabilityTimestamp = (timestamp: string): number => {
  const parsed = parseInt(timestamp, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const mapReliabilityInfluencesToRows = (
  influences: ReliabilityScoreInfluence[],
  lookups: ReliabilityLookups,
): ReliabilityJobRow[] =>
  influences
    .map((influence) => {
      const classificationId =
        influence.job?.classification?.id ?? influence.job?.classificationId;
      const storedClassification = classificationId
        ? lookups.getClassification(classificationId)
        : null;
      const classificationGroupId =
        influence.job?.classification?.classificationGroupId ??
        storedClassification?.classificationGroupId;

      return {
        id: influence.id,
        jobId: influence.jobId,
        date: influence.job?.startDate ?? '',
        clientName:
          influence.client?.name ??
          lookups.getClientName(influence.clientId) ??
          '-',
        skillSet:
          influence.job?.classification?.name ??
          storedClassification?.name ??
          '-',
        industry: classificationGroupId
          ? (lookups.getIndustryName(classificationGroupId) ?? '-')
          : '-',
        postedDate: influence.job?.createdAt ?? '',
        direction: influence.direction,
      };
    })
    .sort(
      (a, b) =>
        parseReliabilityTimestamp(b.date) - parseReliabilityTimestamp(a.date) ||
        parseReliabilityTimestamp(b.postedDate) -
          parseReliabilityTimestamp(a.postedDate),
    );

export const isCancelledDirection = (
  direction: ReliabilityScoreInfluenceDirection,
): boolean =>
  direction === ReliabilityScoreInfluenceDirection.Negative ||
  direction === ReliabilityScoreInfluenceDirection.Excluded;

export const formatReliabilityDate = (
  timestamp: string | null | undefined,
): string => {
  if (!timestamp) return '-';
  const parsed = parseInt(timestamp, 10);
  if (isNaN(parsed)) return '-';
  return dayjs(parsed).format('D MMM YYYY');
};
